import { typographyTokens, surfaceTokens, ACTIVE_SURFACE, ACTIVE_SURFACE_MUTED, layoutTokens } from "@/shared/theme";
import { Box, Button, ButtonBase, Paper, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { useRememberedPageState } from "@/features/preferences/page-state";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/app/context/AuthContext";
import { EditorialPageHeader, EvidenceLabel } from "@/shared/components";
import { ChesslaLoadingMark } from "@/shared/components/ChesslaLoadingMark";
import { conciseOpeningName } from "@/shared/utils/chess";
import {
  type FocusCohort,
  type DevelopmentArea,
  type PatternStat,
  type PatternTrendStats,
  type PrinciplePatternExample,
} from "./patterns.api";
import { usePatterns } from "./usePatterns";
import { formatMotif } from "./format";
import {
  DevelopmentOverview,
  InfoTitle,
  MicroLabel,
  PatternEvidenceLesson,
  PatternSignalRow,
  PrinciplePatterns,
  ReviewSurface,
  TrackedPatterns,
} from "./components";
import { trackProductEvent } from "@/shared/analytics/ga4";
import { parsePatternNavigation, patternWorkspaceUrl } from "./pattern-navigation";
import { patternEvidenceLabels, patternExamplePly, specificPatternStats } from "./pattern-dossier";
import { PatternMovementRail } from "./components/EvidenceRail";
import { PatternPicker } from "./components/PatternPicker";
import { PerformanceSelection, usePerformanceSelection } from "@/features/performance-selection";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { principlePresentation } from "@chessla/shared";

const motifBeginnerExplanation = (motif: string): string => {
  switch (motif) {
    case "blunder":
      return "A move that seriously worsened your position according to verified analysis.";
    case "mistake":
      return "A meaningful error that gave away part of your position, but was less severe than a blunder.";
    case "imprecision":
      return "A small inaccuracy where a stronger move was available.";
    case "time_pressure":
    case "low_time":
      return "Costly decisions that happened while your clock was running low.";
    case "missed_check":
      return "A verified position where a stronger legal check was available.";
    case "missed_capture":
      return "A verified position where a stronger legal capture was available.";
    case "hanging_piece":
      return "A piece could be won because it was left undefended or could not be saved.";
    case "fork":
      return "One piece could attack two valuable targets at the same time.";
    case "pin":
      return "A piece could not safely move because doing so would expose a more valuable piece or the king.";
    case "skewer":
      return "A valuable front piece is attacked and, after it moves, a piece behind it can be won.";
    case "discovered_attack":
      return "Moving one piece reveals an attack from another piece behind it.";
    case "back_rank_threat":
      return "The king is vulnerable on its back rank because it has too few escape squares.";
    case "deflection":
      return "A defender is forced away from the piece or square it must protect.";
    case "sacrifice":
      return "Material is deliberately given up for a concrete stronger payoff.";
    case "zwischenzug":
      return "An in-between forcing move is played before the expected recapture or reply.";
    case "mating_net":
      return "A sequence of forcing moves traps the king and leads to checkmate.";
    case "overloaded_defender":
      return "One defending piece has too many important jobs and cannot perform all of them.";
    default:
      return "A recurring decision pattern found in your analyzed games.";
  }
};

const nextBoardHabit = (motif: string): string => {
  switch (motif) {
    case "blunder":
      return "Before committing, check every capture, check, and threat your opponent has.";
    case "mistake":
      return "When the position changes, compare your move with one calm alternative.";
    case "imprecision":
      return "In quiet positions, improve your worst-placed piece before pushing.";
    case "missed_check":
      return "Before every move, scan every legal check and ask where the king must go.";
    case "missed_capture":
      return "Scan checks, captures, and threats; calculate the safest forcing capture first.";
    case "hanging_piece":
      return "Before releasing the piece, count its attackers and defenders on the destination square.";
    case "time_pressure":
    case "low_time":
      return "Use your opponent's turn to choose your candidate moves before the clock becomes the problem.";
    case "fork":
      return "Before moving, scan every forcing square from which one piece attacks two valuable targets.";
    case "pin":
    case "skewer":
      return "Trace every open rank, file, and diagonal through the target before choosing your move.";
    case "discovered_attack":
      return "When a piece moves, inspect the line it vacates as carefully as its destination.";
    case "back_rank_threat":
    case "mating_net":
      return "Count the king's legal flight squares before calculating checks.";
    case "deflection":
    case "overloaded_defender":
      return "Name each defender and ask what else falls if that defender is forced away.";
    case "zwischenzug":
      return "Before an automatic recapture, look once for a forcing check or stronger capture.";
    case "sacrifice":
      return "Calculate the forced reply and concrete payoff before rejecting a material investment.";
    default:
      return "Open a matching game and review the position before the decision, not just the result.";
  }
};

const stringValue = (value: unknown): string | null => (typeof value === "string" ? value : null);

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

const tacticalEvidenceSummary = (motif: string, evidence: Record<string, unknown> | undefined): string | null => {
  if (!evidence) return null;
  const proof =
    typeof evidence.proof === "object" && evidence.proof != null ? (evidence.proof as Record<string, unknown>) : null;
  const depth = proof && typeof proof.analysisDepth === "number" ? proof.analysisDepth : null;
  const suffix = depth == null ? "" : ` · DEPTH ${depth}`;
  switch (motif) {
    case "fork":
      return `Fork from ${stringValue(evidence.attacker) ?? "the tactic square"} targeting ${stringList(evidence.targets).join(" and ")}${suffix}`;
    case "pin":
      return `Pin on ${stringValue(evidence.pinned) ?? "the target"} against the king on ${stringValue(evidence.king) ?? "its line"}${suffix}`;
    case "skewer":
      return `Skewer through ${stringValue(evidence.front) ?? "the front target"} to ${stringValue(evidence.back) ?? "the rear target"}${suffix}`;
    case "discovered_attack":
      return `Line uncovered from ${stringValue(evidence.vacated) ?? "the vacated square"}${suffix}`;
    case "back_rank_threat":
      return `Forced back-rank finish on ${stringValue(evidence.threatTo) ?? "the back rank"}${suffix}`;
    case "deflection":
      return `Defender forced from ${stringValue(evidence.defenderFrom) ?? "its square"}; ${stringValue(evidence.target) ?? "the target"} falls${suffix}`;
    case "overloaded_defender":
      return `${stringValue(evidence.defender) ?? "One defender"} was carrying ${stringValue(evidence.firstTarget) ?? "two"} and ${stringValue(evidence.secondTarget) ?? "targets"}${suffix}`;
    case "zwischenzug":
      return `In-between move before the capture on ${stringValue(evidence.delayedCaptureAt) ?? "the target"}${suffix}`;
    case "sacrifice":
      return `${stringValue(evidence.sacrificedRole) ?? "Piece"} sacrifice on ${stringValue(evidence.sacrificedOn) ?? "the tactic square"}${suffix}`;
    case "mating_net":
      return `Forced mate in ${typeof evidence.mateIn === "number" ? evidence.mateIn : "?"}${suffix}`;
    case "missed_check":
    case "missed_capture":
    case "hanging_piece":
      return `Legal best move ${stringValue(proof?.bestMoveSan) ?? ""}${suffix}`.trim();
    default:
      return null;
  }
};

const shortDate = (value: string | null): string =>
  value == null
    ? "No date recorded"
    : new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));

const percentageLabel = (value: number | null): string => (value == null ? "NO CLOCK DATA" : `${value}% ERROR RATE`);

const recentFormCopy = (trend: PatternTrendStats): { title: string; info: string } => {
  if (trend.errorMovesPerGameDelta == null || !trend.recent || !trend.prior) {
    return {
      title: "GIVE NIKOLA A FEW MORE GAMES.",
      info: "Nikola needs two equally sized groups of analyzed games before calling a trend. Keep playing—the signal will sharpen.",
    };
  }

  const comparison = `Your latest ${trend.cohortSize} games averaged ${trend.recent.errorMovesPerGame} costly moves per game, compared with ${trend.prior.errorMovesPerGame} in the previous ${trend.cohortSize}.`;
  if (trend.errorMovesPerGameDelta > 0) {
    return {
      title: "MORE IS SLIPPING THROUGH.",
      info: `More costly decisions are slipping through lately. ${comparison} This tracks move quality—not rating or results.`,
    };
  }
  if (trend.errorMovesPerGameDelta < 0) {
    return {
      title: "YOU'RE CLOSING THE GAPS.",
      info: `Sharper work: fewer costly decisions are getting through. ${comparison} Lower is better—keep that line.`,
    };
  }
  return {
    title: "THE LINE HOLDS. FOR NOW.",
    info: `Your costly-move rate has not shifted yet. ${comparison} One cleaner decision per game can break the tie.`,
  };
};

const PATTERN_QUALITY_GAMES = 10;

const PatternWorkspaceHeader = ({ cohort }: { cohort: FocusCohort }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1.5,
      flexWrap: "wrap",
      mb: 2.5,
      pb: 1.25,
      borderBottom: layoutTokens.dividerBorder,
      borderColor: "divider",
    }}
  >
    <Box>
      <MicroLabel>STEP 02 / {cohort.toUpperCase()}</MicroLabel>
      <Typography sx={{ mt: 0.25, fontSize: "caption.fontSize", color: "text.secondary" }}>
        Evidence and Focus from this format only.
      </Typography>
    </Box>
    <Typography sx={{ color: "text.secondary", fontSize: "caption.fontSize", fontWeight: "fontWeightBold" }}>
      CHANGE SCOPE ABOVE
    </Typography>
  </Box>
);

interface PatternWorkspaceProps {
  focusCohort: FocusCohort;
  exactTimeControl?: string;
  navigation: ReturnType<typeof parsePatternNavigation>;
  embedded?: boolean;
}

const PatternWorkspace = ({ focusCohort, exactTimeControl, navigation, embedded = false }: PatternWorkspaceProps) => {
  const patternCardRef = useRef<HTMLDivElement>(null);
  const principleLessonRef = useRef<HTMLDivElement>(null);
  const { profile } = useAuth();
  const entitlementSnapshot = useEntitlementsStore((state) => state.snapshot);
  const patternsAllowed = Boolean(
    entitlementSnapshot &&
    (entitlementSnapshot.coreOpenBeta === true ||
      entitlementSnapshot.membership.active ||
      entitlementSnapshot.trial.patternsUnlocked),
  );
  const { stats, isLoading, error } = usePatterns(focusCohort, exactTimeControl, patternsAllowed);
  const navigate = useNavigate();
  const [selectedMotif, setSelectedMotif] = useRememberedPageState<string | null>(
    `patterns:${focusCohort}:${exactTimeControl ?? "all"}:motif`,
    navigation.motif,
  );
  const [selectedArea, setSelectedArea] = useRememberedPageState<DevelopmentArea["key"] | null>(
    `patterns:${focusCohort}:${exactTimeControl ?? "all"}:area`,
    null,
  );
  const [principleSelection, setPrincipleSelection] = useRememberedPageState<{
    gameId: string;
    ply: number;
    principle: string;
  } | null>(`patterns:${focusCohort}:${exactTimeControl ?? "all"}:principle`, null);
  const selectedPrincipleExample =
    stats?.principleSignals
      ?.find((pattern) => pattern.principle === principleSelection?.principle)
      ?.examples.find(
        (example) => example.gameId === principleSelection?.gameId && example.ply === principleSelection?.ply,
      ) ?? null;
  const setSelectedPrincipleExample = (example: PrinciplePatternExample | null) =>
    setPrincipleSelection(
      example ? { gameId: example.gameId, ply: example.ply, principle: example.signal.principle } : null,
    );

  useEffect(() => {
    if (navigation.motif) setSelectedMotif(navigation.motif);
  }, [navigation.motif, setSelectedMotif]);

  useEffect(() => {
    if (stats) trackProductEvent("patterns_viewed");
  }, [stats]);
  if (profile === null && !isLoading) {
    return (
      <Box
        sx={{
          minHeight: layoutTokens.screenMinHeight,
          maxWidth: layoutTokens.workspaceMaxWidth,
          mx: "auto",
          py: layoutTokens.workspacePagePadding,
        }}
      >
        <Typography variant="h6" sx={{ color: "primary.main", mb: 1 }}>
          Profile not linked
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Link your Chess.com account on your{" "}
          <RouterLink to="/profile" style={{ color: "primary.main", textDecoration: "underline" }}>
            profile
          </RouterLink>{" "}
          to build a pattern scan.
        </Typography>
      </Box>
    );
  }

  if (!entitlementSnapshot || isLoading) {
    return <ChesslaLoadingMark fillContent={!embedded} page="Patterns" label="Nikola is scanning your patterns" />;
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: layoutTokens.screenMinHeight,
          maxWidth: layoutTokens.workspaceMaxWidth,
          mx: "auto",
          py: layoutTokens.workspacePagePadding,
        }}
      >
        <Typography sx={{ color: "secondary.main" }}>{error}</Typography>
      </Box>
    );
  }

  const analyzedGames = patternsAllowed
    ? (stats?.analyzedGameCount ?? 0)
    : entitlementSnapshot.trial.selectedGames.filter((game) => game.locked).length;
  const remainingGames = Math.max(0, PATTERN_QUALITY_GAMES - analyzedGames);
  const onboardingLocked = !patternsAllowed && !entitlementSnapshot.onboarding.complete;
  const clubLocked = !patternsAllowed && entitlementSnapshot.onboarding.complete && !entitlementSnapshot.trial.eligible;
  if (!patternsAllowed || !stats || analyzedGames < PATTERN_QUALITY_GAMES) {
    return (
      <Box
        sx={{
          minHeight: layoutTokens.screenMinHeight,
          maxWidth: layoutTokens.workspaceMaxWidth,
          mx: "auto",
          py: layoutTokens.workspacePagePadding,
        }}
      >
        <Box sx={{ maxWidth: layoutTokens.readingMaxWidth }}>
          <PatternWorkspaceHeader cohort={focusCohort} />
          <MicroLabel>♜ Pattern scan</MicroLabel>
          <Typography
            sx={{
              mt: 1,
              fontSize: { xs: "h4.fontSize", md: "h3.fontSize" },
              fontWeight: "fontWeightBold",
              letterSpacing: typographyTokens.tracking.heading,
              lineHeight: typographyTokens.lineHeight.heading,
            }}
          >
            {onboardingLocked
              ? "Finish your first review to start finding repeat moves."
              : clubLocked
                ? "Join Nikola's Club to unlock Patterns."
                : "Give Nikola ten games to find your repeat moves."}
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              maxWidth: layoutTokens.detailMaxWidth,
              color: "text.secondary",
              lineHeight: typographyTokens.lineHeight.body,
            }}
          >
            {clubLocked
              ? "Club keeps your analyzed library available for format-specific pattern scans."
              : "Patterns need repetition. Nikola reviews this format separately, so a Daily habit never gets mixed with Bullet, Blitz, or Rapid."}
          </Typography>
          {!clubLocked && (
            <Box
              sx={{
                bgcolor: "background.default",
                border: layoutTokens.dividerBorder,
                borderColor: "divider",
                mt: 3,
                p: 2.5,
              }}
            >
              <Box sx={{ alignItems: "baseline", display: "flex", justifyContent: "space-between" }}>
                <MicroLabel>Games reviewed</MicroLabel>
                <Typography sx={{ color: "primary.main", fontWeight: "fontWeightBold" }}>
                  {analyzedGames} / {PATTERN_QUALITY_GAMES}
                </Typography>
              </Box>
              <Box
                aria-label={`${analyzedGames} of ${PATTERN_QUALITY_GAMES} games reviewed`}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={PATTERN_QUALITY_GAMES}
                aria-valuenow={analyzedGames}
                sx={{ bgcolor: "divider", height: layoutTokens.evidenceRailHeight, mt: 1 }}
              >
                <Box
                  sx={{
                    bgcolor: "primary.main",
                    height: "100%",
                    width: `${(analyzedGames / PATTERN_QUALITY_GAMES) * 100}%`,
                  }}
                />
              </Box>
              <Typography sx={{ color: "text.secondary", fontSize: "caption.fontSize", mt: 1 }}>
                Analyze {remainingGames} more game{remainingGames === 1 ? "" : "s"} to unlock the scan.
              </Typography>
            </Box>
          )}
          <Button
            component={RouterLink}
            to={clubLocked ? "/subscription" : "/analyze"}
            variant="contained"
            sx={{ mt: 2.5 }}
          >
            {clubLocked ? "Join Nikola's Club" : onboardingLocked ? "Finish onboarding" : "Analyze more games"}
          </Button>
        </Box>
      </Box>
    );
  }

  const developmentAreas = stats.developmentAreas ?? [];
  const selectedDevelopmentArea = developmentAreas.find((area) => area.key === selectedArea) ?? null;
  const supportedPatterns = specificPatternStats(stats.missedTactics);
  const patterns = selectedDevelopmentArea
    ? supportedPatterns.filter((pattern) => selectedDevelopmentArea.patterns.includes(pattern.motif))
    : supportedPatterns;
  const loudestPattern = patterns[0] ?? null;
  const watchedStatuses = new Set(["accepted", "pending", "inconclusive"]);
  const focusedPattern =
    stats.focus.focus?.type === "tactical" && watchedStatuses.has(stats.focus.focus.status)
      ? (patterns.find((pattern) => pattern.motif === stats.focus.focus?.key) ?? null)
      : null;
  const topPattern = patterns.find((pattern) => pattern.motif === selectedMotif) ?? focusedPattern ?? loudestPattern;
  const selectedPrinciplePattern =
    stats.principleSignals?.find((pattern) => pattern.principle === selectedPrincipleExample?.signal.principle) ?? null;
  const scrollToEvidence = (target: { current: HTMLElement | null }) =>
    requestAnimationFrame(() =>
      target.current?.scrollIntoView({
        block: "start",
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      }),
    );
  const scrollToPattern = () => scrollToEvidence(patternCardRef);
  /** Picking from inside the pattern card keeps the reader where they are; picks from above bring the card in. */
  const showPatternEvidence = (pattern: PatternStat, scroll = true) => {
    setSelectedPrincipleExample(null);
    setSelectedMotif(pattern.motif);
    // Adding a format to a locally selected scope would reinitialize the performance filters.
    if (navigation.cohort === focusCohort)
      void navigate(patternWorkspaceUrl(focusCohort, pattern.motif), { replace: true, preventScrollReset: true });
    if (scroll) scrollToPattern();
  };
  const selectDevelopmentArea = (key: DevelopmentArea["key"] | null) => {
    const area = developmentAreas.find((item) => item.key === key);
    const nextPatterns = area
      ? supportedPatterns.filter((pattern) => area.patterns.includes(pattern.motif))
      : supportedPatterns;
    const nextPattern = nextPatterns.find((pattern) => pattern.motif === topPattern?.motif) ?? nextPatterns[0];
    setSelectedArea(key);
    setSelectedPrincipleExample(null);
    setSelectedMotif(nextPattern?.motif ?? null);
    if (navigation.cohort === focusCohort)
      void navigate(patternWorkspaceUrl(focusCohort, nextPattern?.motif), { replace: true, preventScrollReset: true });
    scrollToPattern();
  };
  const reviewExample = (example: PatternStat["examples"][number]) => {
    const ply = patternExamplePly(example.ply);
    void navigate(
      ply == null
        ? `/games/${example.gameId}`
        : `/games/${example.gameId}?ply=${ply}&move=${example.moveNumber}&san=${encodeURIComponent(example.san)}`,
    );
  };
  const reviewGame = (gameId: string) => {
    void navigate(`/games/${gameId}`);
  };
  const reviewCanonicalDrill = () => {
    const drill = stats.drills[0];
    if (drill) reviewExample(drill);
  };
  const stages = [
    { label: "Opening", range: "MOVES 1–10", count: stats.stageErrorCounts.opening },
    { label: "Middle", range: "MOVES 11–30", count: stats.stageErrorCounts.middlegame },
    { label: "Late", range: "MOVE 31+", count: stats.stageErrorCounts.late },
  ];
  const highestStage = stages.reduce((highest, stage) => (stage.count > highest.count ? stage : highest), stages[0]);
  const trend = stats.recentVsPriorTrend;
  const recentForm = recentFormCopy(trend);
  const tacticalVerification = stats.tacticalVerification ?? {
    verifiedGames: 0,
    needsReanalysisGames: analyzedGames,
  };
  const tacticalCoverageIncomplete = tacticalVerification.needsReanalysisGames > 0;
  const hasVerifiedTacticalSample = tacticalVerification.verifiedGames > 0;
  const dossierLabels = topPattern
    ? patternEvidenceLabels({
        cohort: focusCohort,
        analyzedGames,
        occurrences: topPattern.count,
        affectedGames: topPattern.gameCount,
        distinctDates: topPattern.distinctDates ?? 0,
        maxGames: stats.evidenceWindow?.maxGames ?? 50,
        maxAgeDays: stats.evidenceWindow?.maxAgeDays ?? 180,
      })
    : [];

  return (
    <Box
      sx={{
        minHeight: "100%",
        maxWidth: layoutTokens.workspaceMaxWidth,
        mx: "auto",
        py: layoutTokens.workspacePagePadding,
      }}
    >
      <Box sx={{ pb: 5 }}>
        <Box
          sx={{
            "& h1": {
              fontFamily: "fontFamily",
              fontSize: "h3.fontSize",
              letterSpacing: typographyTokens.tracking.heading,
              lineHeight: typographyTokens.lineHeight.heading,
              maxWidth: "none",
              overflowWrap: "normal",
            },
          }}
        >
          <EditorialPageHeader
            kicker={`STEP 02 / ${focusCohort.toUpperCase()}`}
            title="Know what to work on."
            metadata={dossierLabels.map(({ kind, text }) => (
              <EvidenceLabel key={kind} kind={kind}>
                {text}
              </EvidenceLabel>
            ))}
            action={
              <Typography sx={{ color: "text.secondary", fontSize: "caption.fontSize", fontWeight: "fontWeightBold" }}>
                CHANGE SCOPE ABOVE
              </Typography>
            }
          />
        </Box>
        <DevelopmentOverview
          areas={developmentAreas}
          analyzedGames={analyzedGames}
          selected={selectedArea}
          onSelect={selectDevelopmentArea}
        />
        <TrackedPatterns
          value={stats.focus}
          onSelect={(key) => {
            const pattern = supportedPatterns.find((item) => item.motif === key);
            if (pattern) showPatternEvidence(pattern);
          }}
        />

        {topPattern ? (
          <Paper
            ref={patternCardRef}
            data-testid="pattern-detail-card"
            sx={{
              scrollMarginTop: 64,
              mt: layoutTokens.sectionGap,
              bgcolor: ACTIVE_SURFACE,
              color: "background.default",
              borderColor: ACTIVE_SURFACE,
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 2, bgcolor: "background.paper", color: "text.primary" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography data-testid="active-pattern-scope" role="status" sx={{ fontSize: "body2.fontSize" }}>
                  {selectedDevelopmentArea
                    ? `${selectedDevelopmentArea.label} · ${patterns.length} patterns`
                    : "All patterns"}
                  {` · ${focusCohort[0].toUpperCase()}${focusCohort.slice(1)}`}
                </Typography>
                {selectedDevelopmentArea && (
                  <Button size="small" onClick={() => selectDevelopmentArea(null)}>
                    Show all patterns
                  </Button>
                )}
              </Box>
              <PatternPicker
                patterns={patterns}
                selected={topPattern.motif}
                onSelect={(motif) => {
                  const pattern = patterns.find((item) => item.motif === motif);
                  if (pattern) showPatternEvidence(pattern, false);
                }}
              />
            </Box>
            <ButtonBase
              disabled={topPattern.examples.length === 0}
              onClick={() => {
                setSelectedPrincipleExample(null);
                requestAnimationFrame(() =>
                  document.querySelector('[data-testid="pattern-evidence"]')?.scrollIntoView({ block: "start" }),
                );
              }}
              sx={{
                display: "block",
                width: "100%",
                textAlign: "left",
                "&:not(.Mui-disabled):hover": { bgcolor: surfaceTokens.inverseHover },
                "&:focus-visible": {
                  outline: `${layoutTokens.structuralBorder}px solid`,
                  outlineColor: "background.default",
                  outlineOffset: -2,
                },
              }}
            >
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(0, 1fr)" } }}>
                <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                  <MicroLabel inverse>
                    {topPattern === loudestPattern
                      ? "Recurring pattern / real evidence"
                      : "Selected pattern / real evidence"}
                  </MicroLabel>
                  <InfoTitle
                    inverse
                    info={motifBeginnerExplanation(topPattern.motif)}
                    containerSx={{ mt: 0.75 }}
                    titleSx={{
                      fontFamily: "fontFamily",
                      fontSize: { xs: "h5.fontSize", sm: "h4.fontSize" },
                      fontWeight: "fontWeightBold",
                      letterSpacing: typographyTokens.tracking.heading,
                      lineHeight: typographyTokens.lineHeight.heading,
                    }}
                  >
                    {formatMotif(topPattern.motif)}
                  </InfoTitle>
                  <Typography
                    sx={{
                      mt: 1.25,
                      color: ACTIVE_SURFACE_MUTED,
                      fontFamily: "fontFamily",
                      fontSize: "body1.fontSize",
                      lineHeight: typographyTokens.lineHeight.body,
                      maxWidth: layoutTokens.detailMaxWidth,
                    }}
                  >
                    {topPattern.gameCount} of {topPattern.volume?.analyzedGames ?? analyzedGames} games contained this
                    pattern. {motifBeginnerExplanation(topPattern.motif)}
                  </Typography>
                  <PatternMovementRail inverse movement={topPattern.movement} />
                </Box>
                <Box
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderTop: {
                      xs: `${layoutTokens.dividerBorder}px solid ${surfaceTokens.inverseDivider}`,
                      md: "none",
                    },
                    borderLeft: { md: `${layoutTokens.dividerBorder}px solid ${surfaceTokens.inverseDivider}` },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "fontFamily",
                      fontSize: "h6.fontSize",
                      fontWeight: "fontWeightBold",
                      lineHeight: typographyTokens.lineHeight.heading,
                    }}
                  >
                    {topPattern.gameCount} of {topPattern.volume?.analyzedGames ?? analyzedGames} games
                  </Typography>
                  <MicroLabel inverse>Personal evidence</MicroLabel>
                  <Typography
                    sx={{
                      mt: 1.25,
                      color: ACTIVE_SURFACE_MUTED,
                      fontFamily: "fontFamily",
                      fontSize: "body2.fontSize",
                      lineHeight: typographyTokens.lineHeight.body,
                    }}
                  >
                    Evidence details: {topPattern.count} verified moments. Typical evaluation loss:{" "}
                    {((topPattern.medianCpl ?? topPattern.averageCpl) / 100).toFixed(1)} pawns.
                    {topPattern.decisiveErrorRate == null
                      ? " Decisive-error rate unavailable."
                      : ` ${topPattern.decisiveErrorRate}% were decisive errors.`}
                  </Typography>
                  <Typography sx={{ mt: 0.75, color: ACTIVE_SURFACE_MUTED, fontSize: "caption.fontSize" }}>
                    Last seen {shortDate(topPattern.lastSeenAt)}
                  </Typography>
                  {topPattern.examples.length > 0 && (
                    <Typography
                      sx={{
                        mt: 0.75,
                        fontSize: "caption.fontSize",
                        fontWeight: "fontWeightBold",
                        letterSpacing: typographyTokens.tracking.label,
                      }}
                    >
                      INSPECT THE EVIDENCE ↓
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      mt: 1.75,
                      fontSize: "caption.fontSize",
                      fontWeight: "fontWeightBold",
                      letterSpacing: typographyTokens.tracking.label,
                      textTransform: "uppercase",
                    }}
                  >
                    Next board habit
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.5,
                      color: ACTIVE_SURFACE_MUTED,
                      fontSize: "caption.fontSize",
                      lineHeight: typographyTokens.lineHeight.body,
                    }}
                  >
                    {nextBoardHabit(topPattern.motif)}
                  </Typography>
                </Box>
              </Box>
            </ButtonBase>
            {!selectedPrincipleExample && (
              <PatternEvidenceLesson
                format={focusCohort}
                exactTimeControl={exactTimeControl}
                motif={topPattern.motif}
                description={motifBeginnerExplanation(topPattern.motif)}
                habit={nextBoardHabit(topPattern.motif)}
                occurrences={topPattern.count}
                affectedGames={topPattern.gameCount}
                examples={topPattern.examples}
                onReview={reviewExample}
              />
            )}
          </Paper>
        ) : (
          <Paper sx={{ mt: layoutTokens.sectionGap, p: 3, bgcolor: "background.default", borderColor: "primary.main" }}>
            <MicroLabel>No verified tactical repeat yet</MicroLabel>
            <Typography sx={{ mt: 0.75, fontSize: "h6.fontSize", fontWeight: "fontWeightBold" }}>
              Nikola has not verified a repeating tactical motif for this format.
            </Typography>
          </Paper>
        )}

        <PrinciplePatterns
          patterns={stats.principleSignals ?? []}
          selected={selectedPrincipleExample ? (selectedPrinciplePattern?.principle ?? null) : null}
          onSelectExample={(example) => {
            setSelectedPrincipleExample(example);
            setSelectedMotif(null);
            setSelectedArea(null);
            // Same guard as tactical picks: a bare format URL would reopen the format and clock filters.
            if (navigation.cohort === focusCohort)
              void navigate(patternWorkspaceUrl(focusCohort), { replace: true, preventScrollReset: true });
            scrollToEvidence(principleLessonRef);
          }}
        />
        {selectedPrinciplePattern && selectedPrincipleExample && (
          <Box ref={principleLessonRef} sx={{ scrollMarginTop: 64 }}>
            <PatternEvidenceLesson
              format={focusCohort}
              exactTimeControl={exactTimeControl}
              motif={selectedPrinciplePattern.principle}
              description={principlePresentation(selectedPrinciplePattern.principle).traditionalName}
              habit={principlePresentation(selectedPrinciplePattern.principle).cue}
              occurrences={selectedPrinciplePattern.volume.occurrences}
              affectedGames={selectedPrinciplePattern.affectedGames}
              examples={[]}
              principleExample={selectedPrincipleExample}
              onReview={reviewExample}
            />
          </Box>
        )}

        <Box sx={{ mt: layoutTokens.sectionGap }}>
          <MicroLabel>Micro patterns</MicroLabel>
          <InfoTitle
            info="Patterns that caused meaningful evaluation losses in more than one analyzed game."
            containerSx={{ mt: 0.5 }}
            titleSx={{
              fontSize: { xs: "h5.fontSize", sm: "h5.fontSize" },
              fontWeight: "fontWeightBold",
              letterSpacing: typographyTokens.tracking.heading,
              lineHeight: typographyTokens.lineHeight.heading,
            }}
          >
            NIKOLA&apos;S RED FLAGS.
          </InfoTitle>
          <Typography sx={{ mt: 0.75, color: "text.secondary", fontSize: "caption.fontSize" }}>
            The costly decisions that are repeating often enough to deserve your attention.
          </Typography>

          <Box
            sx={{
              mt: 1.5,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.2fr) minmax(0, 0.8fr)" },
              gap: layoutTokens.contentGap,
              alignItems: "start",
            }}
          >
            <Paper sx={{ bgcolor: "background.default", borderColor: "divider", overflow: "hidden" }}>
              <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2.25, pb: 1 }}>
                <MicroLabel>Every repeat</MicroLabel>
                <InfoTitle
                  info="All recurring costly patterns found across your analyzed games. Select one to review a real example."
                  containerSx={{ mt: 0.5 }}
                  titleSx={{
                    fontSize: "h6.fontSize",
                    fontWeight: "fontWeightBold",
                    letterSpacing: typographyTokens.tracking.heading,
                    lineHeight: typographyTokens.lineHeight.heading,
                  }}
                >
                  THE FULL BOARD.
                </InfoTitle>
              </Box>
              {patterns.length > 0 ? (
                patterns.map((pattern, index) => (
                  <PatternSignalRow
                    key={pattern.motif}
                    pattern={pattern}
                    first={index === 0}
                    selected={pattern.motif === topPattern?.motif}
                    analyzedGames={analyzedGames}
                    evidenceSummary={tacticalEvidenceSummary(pattern.motif, pattern.examples[0]?.evidence)}
                    motifDescription={motifBeginnerExplanation(pattern.motif)}
                    onSelect={showPatternEvidence}
                  />
                ))
              ) : (
                <Typography sx={{ px: 2.5, pb: 2.5, color: "text.secondary", fontSize: "caption.fontSize" }}>
                  No flagged error motifs yet.
                </Typography>
              )}
            </Paper>

            <Paper sx={{ bgcolor: "secondary.main", color: "background.default", borderColor: "secondary.main" }}>
              <ReviewSurface
                inverse
                onClick={stats.missedOpportunityExample ? () => reviewExample(stats.missedOpportunityExample!) : null}
              >
                <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                  <MicroLabel inverse>Missed chances</MicroLabel>
                  <InfoTitle
                    info="How often an opponent blundered and your very next move failed to preserve the opportunity."
                    containerSx={{ mt: 0.5 }}
                    titleSx={{
                      fontSize: "h6.fontSize",
                      fontWeight: "fontWeightBold",
                      letterSpacing: typographyTokens.tracking.heading,
                      lineHeight: typographyTokens.lineHeight.heading,
                    }}
                    inverse
                  >
                    TAKE THE MOMENT.
                  </InfoTitle>
                  <Typography
                    sx={{
                      mt: 2,
                      color: "background.default",
                      fontSize: "h3.fontSize",
                      fontWeight: "fontWeightBold",
                      lineHeight: typographyTokens.lineHeight.heading,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {stats.ignoredThreats}{" "}
                    <Box component="span" sx={{ color: ACTIVE_SURFACE_MUTED, fontSize: "h6.fontSize" }}>
                      / {stats.opponentBlunderCount}
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.75,
                      fontWeight: "fontWeightBold",
                      fontSize: "body2.fontSize",
                      lineHeight: typographyTokens.lineHeight.body,
                    }}
                  >
                    {stats.opponentBlunderCount === 0
                      ? "No opponent blunders recorded yet."
                      : "Follow-up errors / opponent blunders."}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.75,
                      color: ACTIVE_SURFACE_MUTED,
                      fontSize: "caption.fontSize",
                      lineHeight: typographyTokens.lineHeight.body,
                    }}
                  >
                    The first number counts cases where an opponent blunder was immediately followed by one of your
                    moves losing at least 100 centipawns.
                  </Typography>
                  {stats.missedOpportunityExample && (
                    <Typography
                      sx={{
                        mt: 1.25,
                        color: "background.default",
                        fontSize: "caption.fontSize",
                        fontWeight: "fontWeightBold",
                        letterSpacing: typographyTokens.tracking.label,
                      }}
                    >
                      REVIEW THE MOMENT →
                    </Typography>
                  )}
                </Box>
              </ReviewSurface>
            </Paper>
          </Box>
        </Box>

        <Box sx={{ mt: layoutTokens.sectionGap }}>
          <MicroLabel>Where it happens</MicroLabel>
          <InfoTitle
            info="Shows whether your costly moves happen most often in the opening, middlegame, or later phase."
            containerSx={{ mt: 0.5 }}
            titleSx={{
              fontSize: { xs: "h5.fontSize", sm: "h5.fontSize" },
              fontWeight: "fontWeightBold",
              letterSpacing: typographyTokens.tracking.heading,
              lineHeight: typographyTokens.lineHeight.heading,
            }}
          >
            WHERE THE GAPS OPEN.
          </InfoTitle>
          <Typography sx={{ mt: 0.75, color: "text.secondary", fontSize: "caption.fontSize" }}>
            Nikola&apos;s first read: your highest concentration of costly moves is in the{" "}
            {highestStage.label.toLowerCase()} game.
          </Typography>

          <Box
            sx={{
              mt: 1.5,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
              border: layoutTokens.structuralBorder,
              borderColor: "divider",
              bgcolor: "background.default",
            }}
          >
            {stages.map((stage, index) => (
              <Box
                key={stage.label}
                sx={{
                  p: 2,
                  borderTop: { xs: index === 0 ? "none" : layoutTokens.dividerBorder },
                  borderLeft: { sm: index === 0 ? "none" : layoutTokens.dividerBorder },
                  borderColor: "divider",
                }}
              >
                <MicroLabel>{stage.range}</MicroLabel>
                <InfoTitle
                  info={`${stage.label} errors are player moves in ${stage.range.toLowerCase()} that lost at least 100 centipawns.`}
                  containerSx={{ mt: 0.5 }}
                  titleSx={{ fontSize: "body1.fontSize", fontWeight: "fontWeightBold" }}
                >
                  {stage.label.toUpperCase()}
                </InfoTitle>
                <Typography
                  sx={{
                    mt: 1.5,
                    color: stage === highestStage ? "secondary.main" : "primary.main",
                    fontSize: "h4.fontSize",
                    fontWeight: "fontWeightBold",
                    lineHeight: typographyTokens.lineHeight.heading,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {stage.count}
                </Typography>
                <Typography sx={{ mt: 0.25, color: "text.secondary", fontSize: "caption.fontSize" }}>
                  ERROR MOVES
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            mt: layoutTokens.sectionGap,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: layoutTokens.contentGap,
          }}
        >
          <Box sx={{ borderTop: layoutTokens.structuralBorder, borderColor: "primary.main", pt: 1.25 }}>
            <MicroLabel>Clock habits</MicroLabel>
            <InfoTitle
              info="Compares your error rate on rushed moves and moves played with 30 seconds or less remaining."
              containerSx={{ mt: 0.5 }}
              titleSx={{
                fontSize: "h6.fontSize",
                fontWeight: "fontWeightBold",
                letterSpacing: typographyTokens.tracking.heading,
                lineHeight: typographyTokens.lineHeight.heading,
              }}
            >
              THE CLOCK TALKS.
            </InfoTitle>
            <Typography
              sx={{
                mt: 1,
                color: "text.secondary",
                fontSize: "caption.fontSize",
                lineHeight: typographyTokens.lineHeight.body,
              }}
            >
              {stats.clockHabits.totalMeasuredPlayerMoves} moves carried usable clock data.
            </Typography>
            <Box sx={{ mt: 1.25, display: "flex", gap: 3 }}>
              <Box>
                <Typography sx={{ color: "primary.main", fontSize: "h6.fontSize", fontWeight: "fontWeightBold" }}>
                  {percentageLabel(stats.clockHabits.rushedErrorRate)}
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: "caption.fontSize" }}>
                  WHEN YOU MOVE IN {stats.clockHabits.rushedSeconds} SECOND
                  {stats.clockHabits.rushedSeconds === 1 ? "" : "S"} OR LESS
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ color: "primary.main", fontSize: "h6.fontSize", fontWeight: "fontWeightBold" }}>
                  {percentageLabel(stats.clockHabits.timePressureErrorRate)}
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: "caption.fontSize" }}>
                  WITH {stats.clockHabits.timePressureSeconds} SECONDS OR LESS LEFT
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ borderTop: layoutTokens.structuralBorder, borderColor: "primary.main", pt: 1.25 }}>
            <MicroLabel>Recent form</MicroLabel>
            <InfoTitle
              info={recentForm.info}
              containerSx={{ mt: 0.5 }}
              titleSx={{
                fontSize: "h6.fontSize",
                fontWeight: "fontWeightBold",
                letterSpacing: typographyTokens.tracking.heading,
                lineHeight: typographyTokens.lineHeight.heading,
              }}
            >
              {recentForm.title}
            </InfoTitle>
            <Typography
              sx={{
                mt: 1,
                color: "text.secondary",
                fontSize: "caption.fontSize",
                lineHeight: typographyTokens.lineHeight.body,
              }}
            >
              {trend.recent && trend.prior
                ? `Your latest ${trend.cohortSize} games average ${trend.recent.errorMovesPerGame} costly moves per game, versus ${trend.prior.errorMovesPerGame} in the previous ${trend.cohortSize}.`
                : "Nikola needs enough analyzed games to compare two recent groups."}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            mt: layoutTokens.sectionGap,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.1fr) minmax(0, 0.9fr)" },
            gap: layoutTokens.contentGap,
          }}
        >
          <Box
            sx={{
              border: layoutTokens.structuralBorder,
              borderColor: "divider",
              bgcolor: "background.default",
              p: { xs: 2, sm: 2.5 },
            }}
          >
            <MicroLabel>Forcing moves you passed</MicroLabel>
            <InfoTitle
              info={`Counts stronger checks and captures only after legal-position analysis verifies the move and its payoff. ${tacticalVerification.verifiedGames} of ${analyzedGames} analyzed games currently meet that standard.`}
              containerSx={{ mt: 0.5 }}
              titleSx={{
                fontSize: "h6.fontSize",
                fontWeight: "fontWeightBold",
                letterSpacing: typographyTokens.tracking.heading,
                lineHeight: typographyTokens.lineHeight.heading,
              }}
            >
              CHECKS. CAPTURES. LOOK AGAIN.
            </InfoTitle>
            <Box sx={{ mt: 1.5, display: "flex", gap: 3 }}>
              <Box>
                <Typography
                  sx={{
                    color: "primary.main",
                    fontSize: "h4.fontSize",
                    fontWeight: "fontWeightBold",
                    lineHeight: typographyTokens.lineHeight.heading,
                  }}
                >
                  {hasVerifiedTacticalSample || analyzedGames === 0
                    ? stats.tacticalOpportunityCounts.missedChecks
                    : "—"}
                </Typography>
                <Typography sx={{ mt: 0.5, color: "text.secondary", fontSize: "caption.fontSize" }}>
                  MISSED CHECKS{tacticalCoverageIncomplete && hasVerifiedTacticalSample ? " SO FAR" : ""}
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: "primary.main",
                    fontSize: "h4.fontSize",
                    fontWeight: "fontWeightBold",
                    lineHeight: typographyTokens.lineHeight.heading,
                  }}
                >
                  {hasVerifiedTacticalSample || analyzedGames === 0
                    ? stats.tacticalOpportunityCounts.missedCaptures
                    : "—"}
                </Typography>
                <Typography sx={{ mt: 0.5, color: "text.secondary", fontSize: "caption.fontSize" }}>
                  MISSED CAPTURES{tacticalCoverageIncomplete && hasVerifiedTacticalSample ? " SO FAR" : ""}
                </Typography>
              </Box>
            </Box>
            {tacticalCoverageIncomplete ? (
              <Box sx={{ mt: 1.25, borderTop: layoutTokens.dividerBorder, borderColor: "divider", pt: 1.25 }}>
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: "caption.fontSize",
                    lineHeight: typographyTokens.lineHeight.body,
                  }}
                >
                  Based on {tacticalVerification.verifiedGames} verified game
                  {tacticalVerification.verifiedGames === 1 ? "" : "s"}. {tacticalVerification.needsReanalysisGames}{" "}
                  older {tacticalVerification.needsReanalysisGames === 1 ? "report is" : "reports are"} not included
                  yet.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/analyze"
                  size="small"
                  sx={{
                    mt: 0.75,
                    minWidth: 0,
                    p: 0,
                    color: "primary.main",
                    fontSize: "caption.fontSize",
                    fontWeight: "fontWeightBold",
                    letterSpacing: typographyTokens.tracking.label,
                    "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                  }}
                >
                  VERIFY TACTICS →
                </Button>
              </Box>
            ) : (
              <Typography
                sx={{
                  mt: 1,
                  color: "text.secondary",
                  fontSize: "caption.fontSize",
                  lineHeight: typographyTokens.lineHeight.body,
                }}
              >
                Every count passed legal-board geometry, a stable verified payoff, and a replayable principal variation.
              </Typography>
            )}
          </Box>

          <Box sx={{ borderLeft: layoutTokens.structuralBorder, borderColor: "primary.main", pl: 1.5 }}>
            <MicroLabel>Opening context</MicroLabel>
            <InfoTitle
              info="The opening in your analyzed history with the strongest repeated error signal. At least two games are required."
              containerSx={{ mt: 0.5 }}
              titleSx={{
                fontSize: "h6.fontSize",
                fontWeight: "fontWeightBold",
                letterSpacing: typographyTokens.tracking.heading,
                lineHeight: typographyTokens.lineHeight.heading,
              }}
            >
              {stats.topOpeningContext
                ? conciseOpeningName(stats.topOpeningContext.opening).toUpperCase()
                : "NO OPENING SIGNAL YET."}
            </InfoTitle>
            {stats.topOpeningContext && (
              <Typography
                sx={{
                  mt: 1,
                  color: "text.secondary",
                  fontSize: "caption.fontSize",
                  lineHeight: typographyTokens.lineHeight.body,
                }}
              >
                {stats.topOpeningContext.games} analyzed games · {stats.topOpeningContext.errorMoves} error moves ·{" "}
                {stats.topOpeningContext.averageCpl} average CPL. Nikola will only call this out after at least two
                games.
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            mt: layoutTokens.sectionGap,
            border: layoutTokens.structuralBorder,
            borderColor: "primary.main",
            bgcolor: ACTIVE_SURFACE,
            color: "background.default",
          }}
        >
          <ButtonBase
            onClick={reviewCanonicalDrill}
            disabled={stats.drills.length === 0}
            sx={{
              display: "block",
              width: "100%",
              textAlign: "left",
              "&:not(.Mui-disabled):hover": { bgcolor: surfaceTokens.inverseHover },
            }}
          >
            <Box
              sx={{
                p: { xs: 2.5, sm: 3 },
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Box>
                <MicroLabel inverse>Personal drill deck</MicroLabel>
                <InfoTitle
                  inverse
                  info="Opens the server-ranked costly position from your own verified evidence."
                  containerSx={{ mt: 0.5 }}
                  titleSx={{
                    fontSize: { xs: "h5.fontSize", sm: "h4.fontSize" },
                    fontWeight: "fontWeightBold",
                    letterSpacing: typographyTokens.tracking.heading,
                    lineHeight: typographyTokens.lineHeight.heading,
                  }}
                >
                  PULL A POSITION. FIND THE MOVE.
                </InfoTitle>
                <Typography sx={{ mt: 0.75, color: ACTIVE_SURFACE_MUTED, fontSize: "caption.fontSize" }}>
                  Nikola selects a stable evidence-ranked position from your own games. Review it before looking for the
                  answer.
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "caption.fontSize",
                  fontWeight: "fontWeightBold",
                  letterSpacing: typographyTokens.tracking.label,
                }}
              >
                {stats.drills.length > 0 ? "START DRILL →" : "NO DRILLS YET"}
              </Typography>
            </Box>
          </ButtonBase>
        </Box>

        <Box sx={{ mt: layoutTokens.sectionGap }}>
          <MicroLabel>Good habits</MicroLabel>
          <InfoTitle
            info="Positive signals from games where you avoided costly errors or responded accurately to an opponent's mistake."
            containerSx={{ mt: 0.5 }}
            titleSx={{
              fontSize: { xs: "h5.fontSize", sm: "h5.fontSize" },
              fontWeight: "fontWeightBold",
              letterSpacing: typographyTokens.tracking.heading,
              lineHeight: typographyTokens.lineHeight.heading,
            }}
          >
            NIKOLA&apos;S KEEPERS.
          </InfoTitle>
          <Typography sx={{ mt: 0.75, color: "text.secondary", fontSize: "caption.fontSize" }}>
            Give yourself credit for the parts of your game that already hold up.
          </Typography>

          <Box
            sx={{
              mt: 1.5,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              border: layoutTokens.structuralBorder,
              borderColor: "divider",
              bgcolor: "background.default",
            }}
          >
            <ReviewSurface onClick={stats.cleanGameExample ? () => reviewGame(stats.cleanGameExample!.gameId) : null}>
              <Box sx={{ p: { xs: 2.5, sm: 3 }, minHeight: layoutTokens.cardMinHeight }}>
                <MicroLabel>Clean games</MicroLabel>
                <InfoTitle
                  info="Analyzed games where none of your moves lost 100 centipawns or more."
                  containerSx={{ mt: 0.5 }}
                  titleSx={{
                    fontSize: "h6.fontSize",
                    fontWeight: "fontWeightBold",
                    letterSpacing: typographyTokens.tracking.heading,
                    lineHeight: typographyTokens.lineHeight.heading,
                  }}
                >
                  YOU HELD THE LINE.
                </InfoTitle>
                <Typography
                  sx={{
                    mt: 2,
                    color: "primary.main",
                    fontSize: "h3.fontSize",
                    fontWeight: "fontWeightBold",
                    lineHeight: typographyTokens.lineHeight.heading,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {stats.cleanGameCount}{" "}
                  <Box component="span" sx={{ color: "text.secondary", fontSize: "h6.fontSize" }}>
                    / {analyzedGames}
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    mt: 0.75,
                    color: "text.secondary",
                    fontSize: "caption.fontSize",
                    lineHeight: typographyTokens.lineHeight.body,
                  }}
                >
                  Analyzed games with no player move losing 100 or more centipawns.
                </Typography>
                {stats.cleanGameExample && (
                  <Typography
                    sx={{
                      mt: 1,
                      color: "primary.main",
                      fontSize: "caption.fontSize",
                      fontWeight: "fontWeightBold",
                      letterSpacing: typographyTokens.tracking.label,
                    }}
                  >
                    OPEN A CLEAN GAME →
                  </Typography>
                )}
              </Box>
            </ReviewSurface>

            <ReviewSurface onClick={stats.steadyReplyExample ? () => reviewExample(stats.steadyReplyExample!) : null}>
              <Box
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  minHeight: layoutTokens.cardMinHeight,
                  borderTop: { xs: layoutTokens.dividerBorder, sm: "none" },
                  borderLeft: { sm: layoutTokens.dividerBorder },
                  borderColor: "divider",
                }}
              >
                <MicroLabel>Steady replies</MicroLabel>
                <InfoTitle
                  info="Opponent blunders followed by one of your moves that lost fewer than 100 centipawns."
                  containerSx={{ mt: 0.5 }}
                  titleSx={{
                    fontSize: "h6.fontSize",
                    fontWeight: "fontWeightBold",
                    letterSpacing: typographyTokens.tracking.heading,
                    lineHeight: typographyTokens.lineHeight.heading,
                  }}
                >
                  YOU STAYED SHARP.
                </InfoTitle>
                <Typography
                  sx={{
                    mt: 2,
                    color: "primary.main",
                    fontSize: "h3.fontSize",
                    fontWeight: "fontWeightBold",
                    lineHeight: typographyTokens.lineHeight.heading,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {stats.steadyReplyCount}{" "}
                  <Box component="span" sx={{ color: "text.secondary", fontSize: "h6.fontSize" }}>
                    / {stats.opponentBlunderCount}
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    mt: 0.75,
                    color: "text.secondary",
                    fontSize: "caption.fontSize",
                    lineHeight: typographyTokens.lineHeight.body,
                  }}
                >
                  Opponent blunders followed by your reply losing fewer than 100 centipawns.
                </Typography>
                {stats.steadyReplyExample && (
                  <Typography
                    sx={{
                      mt: 1,
                      color: "primary.main",
                      fontSize: "caption.fontSize",
                      fontWeight: "fontWeightBold",
                      letterSpacing: typographyTokens.tracking.label,
                    }}
                  >
                    REVIEW THE REPLY →
                  </Typography>
                )}
              </Box>
            </ReviewSurface>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const PatternsPage = () => {
  const [searchParams] = useSearchParams();
  const navigation = parsePatternNavigation(searchParams);
  const performance = usePerformanceSelection(navigation.cohort, { page: "patterns" });

  if (performance.loading)
    return <ChesslaLoadingMark fillContent page="Patterns" label="Reading your analyzed game library" />;

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Box
        sx={{
          maxWidth: layoutTokens.workspaceMaxWidth,
          mx: "auto",
          px: layoutTokens.workspacePagePadding,
          pt: { xs: 2, md: 3 },
        }}
      >
        {performance.error ? (
          <Typography role="alert" sx={{ color: "secondary.main" }}>
            {performance.error}
          </Typography>
        ) : performance.empty ? (
          <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Typography sx={{ fontWeight: "fontWeightBold" }}>Analyze a game to unlock Patterns.</Typography>
            <Button component={RouterLink} to="/analyze" variant="contained" sx={{ mt: 2 }}>
              Analyze a game
            </Button>
          </Paper>
        ) : (
          performance.selection && (
            <PerformanceSelection
              tourPage="patterns"
              selection={performance.selection}
              formats={performance.formats}
              clocks={performance.clocks}
              resultsLabel="Patterns"
              onEdit={performance.edit}
              onFormat={performance.selectFormat}
              onClock={performance.selectClock}
            >
              {performance.showResults &&
                performance.selectedFormats.map((format) => (
                  <Box component="section" key={format} aria-label={`${format} patterns`}>
                    <PatternWorkspace
                      focusCohort={format}
                      exactTimeControl={
                        performance.query?.format === format ? performance.query.exactTimeControl : undefined
                      }
                      navigation={
                        navigation.cohort === format ? navigation : { step: "format", cohort: null, motif: null }
                      }
                      embedded={performance.selectedFormats.length > 1}
                    />
                  </Box>
                ))}
            </PerformanceSelection>
          )
        )}
      </Box>
    </Box>
  );
};
